import {useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {KTIcon} from '../../../../helpers'
import {
  buildPermissionRowsFromCatalog,
  getAdminPermissionsCatalog,
  getAdminRoleById,
  mapAdminRoleToListRow,
  type PermissionRowFromCatalog,
} from '../../../../../app/modules/auth/core/_requests'

const ACCENT = '#064e3b' // Forest Green
const BORDER = '#d1fae5' // Light emerald border

type ModalViewRoleProps = {
  show: boolean
  setShow: (v: boolean) => void
  curr: any
  /** Opens same delete flow as the table (FastAPI DELETE when applicable). */
  onDelete?: () => void
}

function OverlayInfo({text}: {text: string}) {
  return (
    <span className='text-gray-400 flex-shrink-0' title={text}>
      <i className='fas fa-info-circle fs-7' />
    </span>
  )
}

function PermissionFlag({active, label}: {active: boolean; label: string}) {
  return (
    <span
      className={`fs-8 fw-bold px-3 py-1 rounded-pill ${
        active
          ? 'bg-success bg-opacity-10 text-success border border-success border-opacity-20'
          : 'bg-gray-100 text-gray-400 border border-transparent'
      }`}
    >
      {active && <i className='fas fa-check-circle me-1 fs-9' />}
      {label}
    </span>
  )
}

const ModalViewRole = ({show, setShow, curr, onDelete}: ModalViewRoleProps) => {
  const [roleName, setRoleName] = useState(curr?.name)
  const [roleDescription, setRoleDescription] = useState(curr?.description)
  const [roleSlug, setRoleSlug] = useState<string | undefined>(undefined)
  const [permissions, setPermissons] = useState<PermissionRowFromCatalog[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!show || !curr) return
    let cancelled = false
    setLoading(true)
    ;(async () => {
      try {
        let row = curr
        try {
          const role = await getAdminRoleById(curr.uuid)
          if (cancelled) return
          row = mapAdminRoleToListRow(role)
        } catch {
          /* legacy row or API error — use list row */
        }
        if (cancelled) return
        setRoleName(row.name)
        setRoleDescription(row.description)
        setRoleSlug(row.slug)
        const powers = await getAdminPermissionsCatalog()
        if (cancelled) return
        setPermissons(
          buildPermissionRowsFromCatalog(powers, (code) => {
            const gp = row.group_permissions?.find(
              (item: any) =>
                item?.permission?.name === code || String(item?.permission?.uuid) === code
            )
            if (!gp) return undefined
            return {
              can_read: gp.can_read ?? true,
              can_write: gp.can_write ?? true,
              can_edit: gp.can_edit ?? true,
            }
          })
        )
      } catch (error) {
        console.log(error)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [show, curr?.uuid])

  const handleClose = () => setShow(false)

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size='lg'
      className='modal-view-role'
      contentClassName='rounded-4 border-0 shadow-lg overflow-hidden'
    >
      <div className='modal-content border-0'>
        <div className='modal-header border-0 pb-2 pt-8 px-8 bg-white d-flex align-items-center justify-content-between'>
          <div>
            <h2 className='fw-bolder fs-1 mb-1' style={{color: ACCENT, letterSpacing: '-0.02em'}}>
              View Role Details
            </h2>
            <p className='text-gray-500 fs-7 mb-0 fw-medium'>
              Read-only view of permissions assigned to this role
            </p>
          </div>
          <button
            type='button'
            className='btn btn-icon btn-sm btn-light-emerald rounded-circle shadow-sm'
            style={{backgroundColor: BORDER}}
            onClick={handleClose}
            aria-label='Close'
          >
            <span style={{color: ACCENT}}>
              <KTIcon iconName='cross' className='fs-2' />
            </span>
          </button>
        </div>

        <div className='modal-body px-8 pt-6 pb-2 scroll-y' style={{maxHeight: '70vh'}}>
          <div className='mb-12'>
            <div className='d-flex align-items-center gap-3 mb-6'>
              <div className='h-2px w-30px rounded' style={{backgroundColor: ACCENT}} />
              <h3
                className='fw-bold fs-4 mb-0 text-uppercase'
                style={{color: ACCENT, letterSpacing: '0.05em'}}
              >
                General Information
              </h3>
            </div>

            <div className='row g-6'>
              <div className='col-md-6'>
                <div className='p-5 rounded-4 bg-light bg-opacity-30 border border-gray-100 shadow-sm h-100'>
                  <label
                    className='text-gray-500 fw-bolder text-uppercase mb-2 d-block'
                    style={{fontSize: '0.65rem', letterSpacing: '0.1em'}}
                  >
                    Role Name
                  </label>
                  <div className='fs-4 fw-bolder text-gray-900 lh-sm' style={{color: ACCENT}}>
                    {roleName ?? '—'}
                  </div>
                </div>
              </div>
              <div className='col-md-6'>
                <div className='p-5 rounded-4 bg-light bg-opacity-30 border border-gray-100 shadow-sm h-100'>
                  <label
                    className='text-gray-500 fw-bolder text-uppercase mb-2 d-block'
                    style={{fontSize: '0.65rem', letterSpacing: '0.1em'}}
                  >
                    Slug Identifier
                  </label>
                  <div className='fs-6 fw-bold text-gray-700 font-monospace py-1 px-2 rounded bg-white bg-opacity-50 border border-gray-200 d-inline-block'>
                    {roleSlug ?? '—'}
                  </div>
                </div>
              </div>
              <div className='col-12'>
                <div className='p-5 rounded-4 bg-light bg-opacity-30 border border-gray-100 shadow-sm'>
                  <label
                    className='text-gray-500 fw-bolder text-uppercase mb-2 d-block'
                    style={{fontSize: '0.65rem', letterSpacing: '0.1em'}}
                  >
                    Description
                  </label>
                  <div className='fs-6 fw-medium text-gray-700 lh-base'>
                    {roleDescription ?? '—'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 mb-6'>
              <div className='d-flex align-items-center gap-3'>
                <div className='h-2px w-30px rounded' style={{backgroundColor: ACCENT}} />
                <h3
                  className='fw-bold fs-4 mb-0 text-uppercase'
                  style={{color: ACCENT, letterSpacing: '0.05em'}}
                >
                  Assigned Permissions
                </h3>
              </div>
              <span
                className='badge fs-8 fw-bolder px-4 py-2 rounded-pill shadow-sm'
                style={{backgroundColor: BORDER, color: ACCENT}}
              >
                {permissions.length} Modules Configured
              </span>
            </div>
            <p className='text-gray-500 fs-7 mb-6 fw-medium px-1'>
              The following capabilities are currently active for this role.
            </p>

            {loading ? (
              <div className='text-center py-10 text-gray-500 fw-semibold'>
                <span
                  className='spinner-border spinner-border-sm text-emerald me-3'
                  role='status'
                  style={{color: ACCENT}}
                />
                Fetching active permissions…
              </div>
            ) : (
              <div className='d-flex flex-column gap-4 mb-8'>
                {permissions.map((permission) => (
                  <div
                    key={permission.uuid}
                    className='d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-between gap-4 py-4 px-6 rounded-4 bg-white border border-gray-100 shadow-sm transition-all'
                  >
                    <div className='d-flex align-items-start gap-3 min-w-0 flex-grow-1'>
                      <div className='symbol symbol-40px flex-shrink-0'>
                        <div
                          className='symbol-label rounded-3 bg-light-emerald'
                          style={{backgroundColor: BORDER}}
                        >
                          <i className='fas fa-lock-open fs-4' style={{color: ACCENT}} />
                        </div>
                      </div>
                      <div className='min-w-0 pt-1'>
                        <span className='text-gray-900 fw-bolder fs-6 d-block mb-1 lh-tight'>
                          {permission.label || permission.name}
                        </span>
                        <span className='text-gray-400 fs-8 d-flex align-items-center gap-1 font-monospace'>
                          {permission.name}
                        </span>
                      </div>
                    </div>
                    <div className='d-flex align-items-center gap-2 flex-shrink-0'>
                      <PermissionFlag active={permission.can_read} label='Read' />
                      <PermissionFlag active={permission.can_write} label='Create' />
                      <PermissionFlag active={permission.can_edit} label='Modify' />
                    </div>
                  </div>
                ))}
                {permissions.length === 0 && !loading && (
                  <div className='text-center text-gray-400 py-15 rounded-4 bg-light bg-opacity-50 border border-dashed border-gray-300'>
                    <i className='fas fa-shield-alt fs-2x mb-4 text-gray-200 d-block' />
                    <span className='fs-6 fw-bold'>No specific permissions assigned.</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className='modal-footer border-0 bg-white pt-6 px-8 pb-8 d-flex flex-wrap justify-content-between align-items-center gap-3 shadow-top-sm'>
          <div>
            {onDelete && (
              <button
                type='button'
                className='btn btn-light-danger border border-danger border-opacity-10 fw-bolder px-6 rounded-3 transition-all'
                onClick={onDelete}
              >
                <i className='fas fa-trash-alt me-2 fs-7' />
                Delete Role
              </button>
            )}
          </div>
          <button
            type='button'
            className='btn btn-primary px-12 rounded-3 fw-bolder shadow-sm'
            style={{backgroundColor: ACCENT, borderColor: ACCENT}}
            onClick={handleClose}
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default ModalViewRole
